import {
  useCreateOrderHolidayHours,
  useDeleteOrderHolidayHours,
  useOrderHolidayHours,
  useUpdateOrderHolidayHours,
} from '../hooks/useOrderSetup';
import { ChipInput } from '@/components/ChipInput';
import MonthCalendarPopover from '@/components/MonthCalendarPopover';
import { useT } from '@/i18n';
import type { OrderHolidayHours } from '@restaurant/api-client';
import {
  Block,
  BlockFooter,
  BlockTitle,
  List,
  ListButton,
  ListInput,
  ListItem,
  Navbar,
  NavRight,
  Link as F7Link,
  Page,
  Preloader,
  f7,
} from 'framework7-react';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { useEffect, useMemo, useRef, useState } from 'react';

type OrderType = 'PICKUP' | 'DELIVERY';

/** Compact label for a date pill - matches the reservation date chip format. */
function formatDateChip(value: string): string {
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return '';
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

/** Date-pill trigger styling - mirrors the reservation create/edit date chip. */
const DATE_PILL_STYLE = {
  background: 'rgba(120,120,128,0.12)',
  borderRadius: 6,
  padding: '4px 10px',
  border: 'none',
  cursor: 'pointer',
  fontSize: 17,
  lineHeight: '24px',
  color: 'var(--f7-theme-color)',
  whiteSpace: 'nowrap',
} as const;

type DraftState = {
  name: string;
  startDate: string; // YYYY-MM-DD
  endDate: string;
  openTime: string; // HH:MM
  closeTime: string;
  // '' = no cap (stored null).
  maxOrdersPerSlot: string;
  slotMinutes: string;
  prepTimeMinutes: string;
  schedulingLeadMinutes: string;
  enabled: boolean;
};

function deriveDraft(h: OrderHolidayHours | undefined): DraftState | null {
  if (!h) return null;
  return {
    name: h.name,
    startDate: h.startDate,
    endDate: h.endDate,
    openTime: h.openTime,
    closeTime: h.closeTime,
    maxOrdersPerSlot: h.maxOrdersPerSlot != null ? String(h.maxOrdersPerSlot) : '',
    slotMinutes: String(h.slotMinutes),
    prepTimeMinutes: String(h.prepTimeMinutes),
    schedulingLeadMinutes: String(h.schedulingLeadMinutes),
    enabled: h.enabled,
  };
}

function blankDraft(): DraftState {
  const today = new Date().toISOString().slice(0, 10);
  return {
    name: '',
    startDate: today,
    endDate: today,
    openTime: '11:00',
    closeTime: '15:00',
    maxOrdersPerSlot: '',
    slotMinutes: '30',
    prepTimeMinutes: '15',
    schedulingLeadMinutes: '0',
    enabled: true,
  };
}

/** "" → null, "12" → 12, else NaN (caller treats as invalid). */
function parseCap(v: string): number | null | typeof NaN {
  const t = v.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isInteger(n) && n > 0 ? n : NaN;
}

/** Required positive integer. "" or non-positive → NaN. */
function parsePositive(v: string): number | typeof NaN {
  const n = Number(v.trim());
  return Number.isInteger(n) && n > 0 ? n : NaN;
}

/** Required non-negative integer. "" or negative → NaN. */
function parseNonNeg(v: string): number | typeof NaN {
  const t = v.trim();
  if (t === '') return NaN;
  const n = Number(t);
  return Number.isInteger(n) && n >= 0 ? n : NaN;
}

export default function OrderHolidayHoursFormPage({
  f7route,
}: {
  f7route: { params: { holidayId?: string; orderType?: string } };
}) {
  const { holidayId } = f7route.params;
  const editing = !!holidayId;
  const t = useT();
  const paramType: OrderType = f7route.params.orderType === 'DELIVERY' ? 'DELIVERY' : 'PICKUP';

  // When editing we don't know the channel up front - load both and find by id.
  const { data: pickup } = useOrderHolidayHours('PICKUP');
  const { data: delivery } = useOrderHolidayHours('DELIVERY');
  const all = useMemo(() => [...(pickup ?? []), ...(delivery ?? [])], [pickup, delivery]);
  const holiday = editing ? all.find((h) => h.id === holidayId) : undefined;
  const relType: OrderType = editing ? (holiday?.orderType ?? paramType) : paramType;

  const create = useCreateOrderHolidayHours();
  const update = useUpdateOrderHolidayHours();
  const del = useDeleteOrderHolidayHours();

  const [draft, setDraft] = useState<DraftState | null>(() =>
    editing ? deriveDraft(holiday) : blankDraft(),
  );

  useEffect(() => {
    if (draft) return;
    if (editing && holiday) setDraft(deriveDraft(holiday));
  }, [draft, editing, holiday]);

  const startCalRef = useRef<HTMLButtonElement>(null);
  const endCalRef = useRef<HTMLButtonElement>(null);
  const [startCalOpen, setStartCalOpen] = useState(false);
  const [endCalOpen, setEndCalOpen] = useState(false);

  const back = () => f7.views.current.router.back();
  const sf = t.orderSetup.shiftForm;
  const hf = t.orderSetup.holidayForm;

  const submit = () => {
    if (!draft) return;
    const name = draft.name.trim();
    if (!name) return f7.dialog.alert(hf.nameRequired);

    if (
      !/^\d{4}-\d{2}-\d{2}$/.test(draft.startDate) ||
      !/^\d{4}-\d{2}-\d{2}$/.test(draft.endDate) ||
      draft.endDate < draft.startDate
    ) {
      return f7.dialog.alert(hf.dateRangeInvalid);
    }
    if (
      !/^\d{2}:\d{2}$/.test(draft.openTime) ||
      !/^\d{2}:\d{2}$/.test(draft.closeTime) ||
      draft.openTime === draft.closeTime
    ) {
      return f7.dialog.alert(hf.hoursInvalid);
    }

    const maxOrders = parseCap(draft.maxOrdersPerSlot);
    if (Number.isNaN(maxOrders)) {
      return f7.dialog.alert(hf.capacityInvalid);
    }
    const slot = parsePositive(draft.slotMinutes);
    if (Number.isNaN(slot)) return f7.dialog.alert(sf.slotInvalid);
    const prep = parseNonNeg(draft.prepTimeMinutes);
    const lead = parseNonNeg(draft.schedulingLeadMinutes);
    if (Number.isNaN(prep) || Number.isNaN(lead)) {
      return f7.dialog.alert(hf.prepNoticeInvalid);
    }

    const onError = (e: unknown) => f7.dialog.alert(e instanceof Error ? e.message : sf.saveFailed);
    const done = () => {
      f7.toast.show({ text: t.saved, closeTimeout: 2000, position: 'bottom' });
      back();
    };

    if (editing && holidayId) {
      update.mutate(
        {
          holidayId,
          data: {
            name,
            startDate: draft.startDate,
            endDate: draft.endDate,
            openTime: draft.openTime,
            closeTime: draft.closeTime,
            maxOrdersPerSlot: maxOrders as number | null,
            slotMinutes: slot as number,
            prepTimeMinutes: prep as number,
            schedulingLeadMinutes: lead as number,
            enabled: draft.enabled,
          },
        },
        { onSuccess: done, onError },
      );
    } else {
      create.mutate(
        {
          orderType: relType,
          name,
          startDate: draft.startDate,
          endDate: draft.endDate,
          openTime: draft.openTime,
          closeTime: draft.closeTime,
          maxOrdersPerSlot: maxOrders as number | null,
          slotMinutes: slot as number,
          prepTimeMinutes: prep as number,
          schedulingLeadMinutes: lead as number,
          enabled: draft.enabled,
        },
        { onSuccess: done, onError },
      );
    }
  };

  const remove = () => {
    if (!holidayId) return;
    f7.dialog.confirm(hf.deleteConfirm, t.delete, () => {
      del.mutate(holidayId, {
        onSuccess: () => {
          f7.toast.show({ text: sf.deleted, closeTimeout: 2000, position: 'bottom' });
          back();
        },
        onError: (e) => f7.dialog.alert(e instanceof Error ? e.message : sf.deleteFailed),
      });
    });
  };

  const saving = create.isPending || update.isPending;
  const channel =
    relType === 'DELIVERY'
      ? t.orderSetup.page.delivery.toLowerCase()
      : t.orderSetup.page.pickup.toLowerCase();

  return (
    <Page name="order-holiday-form">
      <Navbar title={editing ? hf.editTitle : hf.newTitle} backLink={t.back}>
        <NavRight>
          <F7Link onClick={saving || !draft ? undefined : submit}>{t.saveShort}</F7Link>
        </NavRight>
      </Navbar>

      {!draft ? (
        <Block className="text-align-center" style={{ paddingTop: 48 }}>
          <Preloader />
        </Block>
      ) : (
        <>
          <BlockTitle>{sf.name}</BlockTitle>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListInput
              type="text"
              placeholder={hf.namePlaceholder}
              value={draft.name}
              onInput={(e) =>
                setDraft((d) => (d ? { ...d, name: (e.target as HTMLInputElement).value } : d))
              }
            />
          </List>

          <BlockTitle>{hf.dates}</BlockTitle>
          <BlockFooter style={{ marginTop: -6 }}>{hf.datesFooter(channel)}</BlockFooter>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListItem title={hf.starts}>
              <button
                slot="after"
                ref={startCalRef}
                type="button"
                className="no-ripple"
                aria-label={hf.startAria}
                onClick={() => setStartCalOpen(true)}
                style={DATE_PILL_STYLE}
              >
                {formatDateChip(draft.startDate) || '-'}
              </button>
            </ListItem>
            <ListItem title={hf.ends}>
              <button
                slot="after"
                ref={endCalRef}
                type="button"
                className="no-ripple"
                aria-label={hf.endAria}
                onClick={() => setEndCalOpen(true)}
                style={DATE_PILL_STYLE}
              >
                {formatDateChip(draft.endDate) || '-'}
              </button>
            </ListItem>
          </List>

          <MonthCalendarPopover
            open={startCalOpen}
            onClose={() => setStartCalOpen(false)}
            anchorEl={startCalRef.current}
            selectedDate={draft.startDate}
            onSelect={(v) => {
              setDraft((d) =>
                d ? { ...d, startDate: v, endDate: d.endDate < v ? v : d.endDate } : d,
              );
              setStartCalOpen(false);
            }}
          />
          <MonthCalendarPopover
            open={endCalOpen}
            onClose={() => setEndCalOpen(false)}
            anchorEl={endCalRef.current}
            selectedDate={draft.endDate}
            minDate={draft.startDate}
            onSelect={(v) => {
              setDraft((d) => (d ? { ...d, endDate: v } : d));
              setEndCalOpen(false);
            }}
          />

          <BlockTitle>{sf.hours}</BlockTitle>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListItem noChevron title={sf.opens}>
              <ChipInput
                slot="after"
                type="time"
                value={draft.openTime}
                onChange={(v) => setDraft((d) => (d ? { ...d, openTime: v } : d))}
              />
            </ListItem>
            <ListItem noChevron title={sf.closes}>
              <ChipInput
                slot="after"
                type="time"
                value={draft.closeTime}
                onChange={(v) => setDraft((d) => (d ? { ...d, closeTime: v } : d))}
              />
            </ListItem>
          </List>

          <BlockTitle>{sf.capacity}</BlockTitle>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListInput
              label={t.orderCapacity.ordersPerSlotLabel}
              type="number"
              placeholder={t.orderCapacity.noCap}
              value={draft.maxOrdersPerSlot}
              onInput={(e) =>
                setDraft((d) =>
                  d ? { ...d, maxOrdersPerSlot: (e.target as HTMLInputElement).value } : d,
                )
              }
            />
            <ListInput
              label={t.orderCapacity.slotLengthLabel}
              type="number"
              placeholder="30"
              value={draft.slotMinutes}
              onInput={(e) =>
                setDraft((d) =>
                  d ? { ...d, slotMinutes: (e.target as HTMLInputElement).value } : d,
                )
              }
            />
            <ListInput
              label={t.orderCapacity.prepTimeLabel}
              type="number"
              placeholder="15"
              value={draft.prepTimeMinutes}
              onInput={(e) =>
                setDraft((d) =>
                  d ? { ...d, prepTimeMinutes: (e.target as HTMLInputElement).value } : d,
                )
              }
            />
            <ListInput
              label={t.orderCapacity.minNoticeLabel}
              type="number"
              placeholder="0"
              value={draft.schedulingLeadMinutes}
              onInput={(e) =>
                setDraft((d) =>
                  d ? { ...d, schedulingLeadMinutes: (e.target as HTMLInputElement).value } : d,
                )
              }
            />
          </List>

          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListItem title={hf.on}>
              <ToggleSwitch
                slot="after"
                checked={draft.enabled}
                onChange={(v) => setDraft((d) => (d ? { ...d, enabled: v } : d))}
              />
            </ListItem>
          </List>
          <BlockFooter>{hf.activeFooter}</BlockFooter>

          {editing && (
            <List strongIos strongMd insetIos insetMd>
              <ListButton color="red" onClick={remove}>
                {hf.deleteHoliday}
              </ListButton>
            </List>
          )}
        </>
      )}
    </Page>
  );
}
